import React, { useEffect, useState } from "react";
import type { ComponentType } from "react";
import ReactDOM from "react-dom/client";
import { Amplify } from "aws-amplify";
import {
  Authenticator,
  Text,
  ThemeProvider,
  View,
} from "@aws-amplify/ui-react";

import "@aws-amplify/ui-react/styles.css";
import "@aws-amplify/ui-react-storage/styles.css";
// Must come last: Vite preserves import order, and these rules override
// the Amplify stylesheets above.
import "@/theme/theme.css";

import { App } from "@/app/App";
import {
  LOGIN_FORM_FIELDS,
  LoginFooter,
  LoginHeader,
} from "@/auth/loginBranding";
import { applyAuthenticatorMessageOverrides } from "@/auth/messages";
import { evidoTheme } from "@/theme/theme";
import {
  AppRole,
  buildStoragePaths,
  resolveRole,
  type StoragePaths,
} from "@/auth/roles";
import { resolveCustomerScope } from "@/auth/customerScope";
import { createRoleStorageBrowser } from "@/storage/browser";

interface StorageBucketConfiguration {
  name: string;
  bucket_name: string;
  aws_region: string;
  paths: StoragePaths;
}

interface StorageConfiguration {
  aws_region: string;
  bucket_name: string;
  buckets: StorageBucketConfiguration[];
}

interface CustomConfiguration {
  STORAGE_ROOT: string;
}

interface AmplifyRuntimeConfiguration {
  version: string;
  auth: unknown;
  storage: StorageConfiguration;
  custom: CustomConfiguration;
}

async function loadRuntimeConfiguration(): Promise<AmplifyRuntimeConfiguration> {
  const response = await fetch("/amplify_outputs.json", {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to load Amplify runtime configuration: ${response.status}`,
    );
  }

  return (await response.json()) as AmplifyRuntimeConfiguration;
}

function withStoragePaths(
  configuration: AmplifyRuntimeConfiguration,
  paths: StoragePaths,
): AmplifyRuntimeConfiguration {
  return {
    ...configuration,
    storage: {
      ...configuration.storage,
      buckets: configuration.storage.buckets.map((bucket) => ({
        ...bucket,
        paths,
      })),
    },
  };
}

type StorageBrowserState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | {
      readonly status: "ready";
      readonly StorageBrowser: ComponentType;
      readonly role: AppRole;
    };

function AuthenticatedApp({
  runtimeConfiguration,
  userName,
  onSignOut,
}: {
  runtimeConfiguration: AmplifyRuntimeConfiguration;
  userName: string;
  onSignOut?: () => void;
}) {
  const [state, setState] = useState<StorageBrowserState>({
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const role = await resolveRole();
        const customerScope =
          role === AppRole.Customer ? await resolveCustomerScope() : undefined;
        const { STORAGE_ROOT: projectPrefixRoot } = runtimeConfiguration.custom;
        Amplify.configure(
          withStoragePaths(
            runtimeConfiguration,
            buildStoragePaths(role, customerScope, projectPrefixRoot),
          ),
        );
        const StorageBrowser = createRoleStorageBrowser(
          role,
          customerScope,
          projectPrefixRoot,
        );
        if (!cancelled) {
          setState({ status: "ready", StorageBrowser, role });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setState({ status: "error", message: String(error) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runtimeConfiguration]);

  if (state.status === "loading") {
    return <Text padding="2rem">Loading your files…</Text>;
  }

  if (state.status === "error") {
    return (
      <View as="pre" padding="2rem" whiteSpace="pre-wrap">
        {state.message}
      </View>
    );
  }

  return (
    <App
      userName={userName}
      role={state.role}
      onSignOut={onSignOut}
      StorageBrowser={state.StorageBrowser}
    />
  );
}

async function bootstrapApplication(): Promise<void> {
  const runtimeConfiguration = await loadRuntimeConfiguration();

  Amplify.configure(runtimeConfiguration);
  applyAuthenticatorMessageOverrides();

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
      <ThemeProvider theme={evidoTheme}>
        <Authenticator
          hideSignUp
          formFields={LOGIN_FORM_FIELDS}
          components={{
            SignIn: { Header: LoginHeader, Footer: LoginFooter },
          }}
        >
          {({ signOut, user }) => (
            <AuthenticatedApp
              runtimeConfiguration={runtimeConfiguration}
              userName={
                user?.signInDetails?.loginId ?? user?.username ?? "User"
              }
              onSignOut={signOut}
            />
          )}
        </Authenticator>
      </ThemeProvider>
    </React.StrictMode>,
  );
}

bootstrapApplication().catch((error: unknown) => {
  const rootElement = document.getElementById("root");

  if (rootElement) {
    const errorBlock = document.createElement("pre");
    errorBlock.textContent = String(error);
    rootElement.replaceChildren(errorBlock);
  }
});
